import { Link, useParams } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import ComicCard from "@/components/feature/ComicCard";
import useSeo from "@/hooks/useSeo";
import { comics, siteMeta } from "@/mocks/comics";

export default function ComicDetailPage() {
  const { id } = useParams<{ id: string }>();
  const comic = comics.find((c) => c.id === id);

  const related = comics.filter((c) => c.id !== id && c.categoryId === comic?.categoryId).slice(0, 4);
  const fallback = comics.filter((c) => c.id !== id && !related.some((r) => r.id === c.id)).slice(0, 4);
  const suggestions = related.length >= 4 ? related : [...related, ...fallback].slice(0, 5);

  const chapterList = Array.from({ length: comic ? Math.min(comic.chapters, 12) : 0 }, (_, i) => comic!.chapters - i);

  useSeo({
    title: comic ? `${comic.title} 韩漫详情 - 韩漫YY榜` : "作品不存在",
    description: comic
      ? `${comic.title}：${comic.intro} 评分${comic.score}，已更新${comic.chapters}话，${comic.status}。韩漫YY榜为你整理完整简介与章节目录。`
      : "作品不存在",
    keywords: comic
      ? `${comic.title},韩漫${comic.title},${comic.category},韩漫yy排行榜`
      : "韩漫yy",
    path: `/comic/${id}`,
    structuredData: comic
      ? [
          {
            "@context": "https://schema.org",
            "@type": "Book",
            name: comic.title,
            author: { "@type": "Person", name: comic.author },
            genre: comic.category,
            inLanguage: "zh-CN",
            description: comic.intro,
            numberOfPages: undefined,
            url: `${window.location.origin}/comic/${comic.id}`,
          },
          {
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "首页", item: window.location.origin },
              {
                "@type": "ListItem",
                position: 2,
                name: "题材分类",
                item: `${window.location.origin}/categories`,
              },
              {
                "@type": "ListItem",
                position: 3,
                name: comic.category.split(" / ")[0],
                item: `${window.location.origin}/category/${comic.categoryId}`,
              },
              { "@type": "ListItem", position: 4, name: comic.title },
            ],
          },
        ]
      : undefined,
  });

  if (!comic) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center">
        <Navbar />
        <div className="text-center">
          <h1 className="font-heading text-2xl text-foreground-950">作品不存在</h1>
          <Link to="/categories" className="mt-4 inline-block rounded-md bg-primary-500 px-6 py-2.5 text-sm text-background-50">
            返回分类
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen">
      <Navbar />
      <main className="pt-16 md:pt-20">
        <section className="border-b border-background-200/70 bg-background-100">
          <div className="mx-auto max-w-[1200px] px-4 md:px-6 py-8 md:py-12">
            <nav aria-label="面包屑" className="text-sm text-foreground-600">
              <ol className="flex flex-wrap items-center gap-2">
                <li><Link to="/" className="hover:text-primary-600">首页</Link></li>
                <li><i className="ri-arrow-right-s-line"></i></li>
                <li><Link to="/categories" className="hover:text-primary-600">题材分类</Link></li>
                <li><i className="ri-arrow-right-s-line"></i></li>
                <li>
                  <Link to={`/category/${comic.categoryId}`} className="hover:text-primary-600">
                    {comic.category.split(" / ")[0]}
                  </Link>
                </li>
                <li><i className="ri-arrow-right-s-line"></i></li>
                <li aria-current="page" className="text-foreground-900">{comic.title}</li>
              </ol>
            </nav>

            <div className="mt-8 flex flex-col gap-8 md:flex-row">
              <div className="mx-auto w-52 shrink-0 md:mx-0 md:w-60">
                <div className="overflow-hidden rounded-lg bg-background-200 shadow-lg aspect-[3/4]">
                  <img src={comic.cover} alt={`${comic.title} 韩漫封面`} className="h-full w-full object-cover object-top" />
                </div>
              </div>

              <div className="flex-1">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="rounded bg-primary-500 px-2.5 py-1 text-xs font-medium text-background-50">{comic.status}</span>
                  <span className="flex items-center gap-1 rounded bg-accent-100 px-2.5 py-1 text-xs font-medium text-accent-800">
                    <i className="ri-star-fill"></i> {comic.score} 分
                  </span>
                  <span className="rounded bg-secondary-100 px-2.5 py-1 text-xs font-medium text-secondary-800">{comic.category}</span>
                </div>
                <h1 className="mt-4 font-heading text-3xl text-foreground-950 md:text-4xl">{comic.title}</h1>
                <p className="mt-2 text-sm text-foreground-600">作者 · {comic.author}</p>

                <div className="mt-5 flex flex-wrap gap-2">
                  {comic.tags.map((t) => (
                    <span key={t} className="rounded-full border border-background-300 bg-background-50 px-3 py-1 text-xs text-foreground-700">
                      #{t}
                    </span>
                  ))}
                </div>

                <div className="mt-6 grid max-w-md grid-cols-3 gap-4 rounded-lg bg-background-50 p-4">
                  <div>
                    <div className="font-heading text-xl text-foreground-950">{comic.chapters}</div>
                    <div className="mt-1 text-xs text-foreground-600">章节</div>
                  </div>
                  <div>
                    <div className="font-heading text-xl text-foreground-950">{comic.views}</div>
                    <div className="mt-1 text-xs text-foreground-600">人气</div>
                  </div>
                  <div>
                    <div className="font-heading text-xl text-foreground-950">{comic.score}</div>
                    <div className="mt-1 text-xs text-foreground-600">评分</div>
                  </div>
                </div>

                <div className="mt-6 flex flex-col gap-3 sm:flex-row">
                  <Link
                    to="/categories"
                    className="inline-flex items-center justify-center gap-2 rounded-md bg-primary-500 px-6 py-3 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600 whitespace-nowrap"
                  >
                    <i className="ri-play-circle-line"></i> 开始阅读
                  </Link>
                  <button
                    type="button"
                    className="inline-flex items-center justify-center gap-2 rounded-md bg-background-50 px-6 py-3 text-sm font-medium text-foreground-800 transition-colors hover:bg-background-200 whitespace-nowrap"
                  >
                    <i className="ri-heart-line"></i> 收藏作品
                  </button>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="mx-auto max-w-[1200px] px-4 md:px-6 py-10 md:py-14">
          <div className="grid grid-cols-1 gap-10 lg:grid-cols-3">
            <div className="lg:col-span-2">
              <h2 className="font-heading text-2xl text-foreground-950">作品简介</h2>
              <p className="mt-4 text-sm leading-relaxed text-foreground-700 md:text-base">{comic.intro}</p>

              <div className="mt-10">
                <div className="flex items-center justify-between">
                  <h2 className="font-heading text-2xl text-foreground-950">章节目录</h2>
                  <span className="text-xs text-foreground-500">共 {comic.chapters} 话</span>
                </div>
                <div className="mt-4 grid grid-cols-2 gap-2.5 sm:grid-cols-3">
                  {chapterList.map((n) => (
                    <Link
                      key={n}
                      to="/categories"
                      className="flex items-center gap-2 rounded-md border border-background-200 bg-background-50 px-3 py-2.5 text-sm text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-700"
                    >
                      <i className="ri-bookmark-line text-xs text-primary-500"></i>
                      第 {n} 话
                    </Link>
                  ))}
                  <div className="flex items-center gap-2 rounded-md bg-background-100 px-3 py-2.5 text-sm text-foreground-600">
                    <i className="ri-lock-line text-xs"></i>
                    更多章节敬请期待
                  </div>
                </div>
              </div>
            </div>

            <aside>
              <h2 className="font-heading text-xl text-foreground-950">相关推荐</h2>
              <div className="mt-4 flex flex-col gap-3">
                {suggestions.map((c) => (
                  <Link key={c.id} to={`/comic/${c.id}`} className="card-lift group flex items-center gap-3 rounded-lg bg-background-100 p-3">
                    <img
                      src={c.cover}
                      alt={`${c.title} 封面`}
                      loading="lazy"
                      className="h-16 w-12 shrink-0 rounded object-cover object-top"
                    />
                    <div className="min-w-0 flex-1">
                      <h3 className="truncate text-sm font-semibold text-foreground-950 group-hover:text-primary-600">{c.title}</h3>
                      <p className="mt-1 truncate text-xs text-foreground-600">{c.category}</p>
                    </div>
                    <div className="shrink-0 text-sm font-medium text-accent-600">{c.score}</div>
                  </Link>
                ))}
              </div>
            </aside>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}